import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { User, UserLimits } from "../types/user";
import api from "../api";
import { setSentryUser, clearSentryUser } from "../sentry";
import {
  getStoredStartOfWeek,
  setStoredStartOfWeek,
  type StartOfWeek,
} from "../utils/dateUtils";

export interface AuthContextType {
  user: User | null;
  limits: UserLimits | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  loading: boolean;
  startOfWeek: StartOfWeek;
  login: (user: User) => void;
  logout: () => void;
  registerUser: (user: User) => void;
  updateUser: (user: User) => void;
  updateStartOfWeek: (value: StartOfWeek) => void;
  startGuestSession: () => Promise<void>;
  refreshLimits: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [limits, setLimits] = useState<UserLimits | null>(null);
  const [startOfWeek, setStartOfWeek] = useState<StartOfWeek>(() =>
    getStoredStartOfWeek(),
  );
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const syncStartOfWeek = useCallback(async () => {
    try {
      const response = await api.get("/users/settings");
      const value = (response.data.start_of_week || "monday") as StartOfWeek;
      setStoredStartOfWeek(value);
      setStartOfWeek(value);
    } catch {
      // Keep the locally stored value on error (e.g. unauthenticated test mocks).
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // US230: the JWT is in an httpOnly cookie, sent automatically.
        // We just call /users/profile to check if we're authenticated.
        const response = await api.get("/users/profile");
        setUser(response.data);
        // Fetch tier-based limits so the UI can pre-check caps
        try {
          const limitsResponse = await api.get("/users/limits");
          setLimits(limitsResponse.data);
        } catch {
          setLimits(null);
        }
        await syncStartOfWeek();
      } catch {
        setUser(null);
        // Use the locally stored preference (or the Monday default) when not
        // authenticated.
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // US255: detect logout in another browser tab via the storage event.
  // When one tab logs out, it writes a timestamp to localStorage.
  // Other tabs detect the change and log out too.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "pmm_logout" && e.newValue) {
        setUser(null);
        setLimits(null);
        clearSentryUser();
        navigate("/login");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [navigate]);

  const login = useCallback(
    (newUser: User) => {
      setUser(newUser);
      setSentryUser({ id: newUser.id, email: newUser.email });
      (async () => {
        try {
          const res = await api.get("/users/limits");
          setLimits(res?.data ?? null);
          await syncStartOfWeek();
        } catch {
          setLimits(null);
        }
      })();
    },
    [syncStartOfWeek],
  );

  const logout = useCallback(() => {
    setUser(null);
    setLimits(null);
    clearSentryUser();
    // US255: broadcast the logout to other browser tabs via localStorage.
    // The storage event listener above will pick this up in other tabs.
    localStorage.setItem("pmm_logout", Date.now().toString());
    navigate("/login");
  }, [navigate]);

  const registerUser = useCallback(
    (newUser: User) => {
      setUser(newUser);
      setSentryUser({ id: newUser.id, email: newUser.email });
      (async () => {
        try {
          const res = await api.get("/users/limits");
          setLimits(res?.data ?? null);
          await syncStartOfWeek();
        } catch {
          setLimits(null);
        }
      })();
    },
    [syncStartOfWeek],
  );

  const startGuestSession = useCallback(async () => {
    // US230: the JWT is set as an httpOnly cookie by the backend.
    // First, create the guest session (the backend sets the cookie).
    await api.post("/auth/guest");

    // Then fetch the profile to get the user data (cookie sent automatically).
    const profileResponse = await api.get("/users/profile");
    const guestUser: User = profileResponse.data;

    setUser(guestUser);
    setSentryUser({ id: guestUser.id, email: guestUser.email });

    try {
      const limitsResponse = await api.get("/users/limits");
      setLimits(limitsResponse.data);
      await syncStartOfWeek();
    } catch {
      setLimits(null);
    }
  }, [syncStartOfWeek]);

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
  }, []);

  const updateStartOfWeek = useCallback((value: StartOfWeek) => {
    setStoredStartOfWeek(value);
    setStartOfWeek(value);
  }, []);

  const refreshLimits = useCallback(async () => {
    try {
      const res = await api.get("/users/limits");
      setLimits(res?.data ?? null);
    } catch {
      setLimits(null);
    }
  }, []);

  const value: AuthContextType = {
    user,
    limits,
    isAuthenticated: user !== null,
    isGuest: user?.is_guest ?? false,
    loading,
    startOfWeek,
    login,
    logout,
    registerUser,
    updateUser,
    updateStartOfWeek,
    startGuestSession,
    refreshLimits,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthContext };
